/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import MDButton from 'components/MDButton';
import {
  FIELD_TYPES,
  ISO_CURRENCIES,
  BOOLEAN_LABEL_PRESETS,
  BOOLEAN_DISPLAY_OPTIONS,
  DEFAULT_FIELD,
  slugifyFieldName,
  formatCurrencyOption,
  getRelationTargetOptions,
} from './customObjectsFieldTypes';

const TYPES_WITH_MAX_LENGTH = ['text', 'long_text', 'url', 'email'];

export default function FieldEditorDialog({ open, field, isNew, onClose, onSave, customObjects }) {
  const [draft, setDraft] = useState(field || DEFAULT_FIELD());
  const [optionText, setOptionText] = useState('');
  const relationTargets = getRelationTargetOptions(customObjects);

  useEffect(() => {
    if (!open) return;
    const next = field ? { ...DEFAULT_FIELD(), ...field } : DEFAULT_FIELD();
    // When editing, treat existing API name as user-owned unless empty
    if (!isNew && next.name) {
      next._nameLocked = true;
    } else if (isNew) {
      next._nameLocked = false;
      next.name = next.label ? slugifyFieldName(next.label) : '';
    }
    setDraft(next);
    setOptionText('');
  }, [field, open, isNew]);

  const update = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const handleLabelChange = (label) => {
    setDraft((prev) => {
      const autoName = slugifyFieldName(label);
      const shouldSyncName = !prev._nameLocked;
      return {
        ...prev,
        label,
        name: shouldSyncName ? autoName : prev.name,
      };
    });
  };

  const handleApiNameChange = (name) => {
    update({
      name: name.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
      _nameLocked: true,
    });
  };

  const handleSave = () => {
    const next = { ...draft };
    if (!next.name?.trim()) next.name = slugifyFieldName(next.label);
    delete next._nameLocked;
    onSave(next);
    onClose();
  };

  const defaultMaxHint = draft.type === 'text' ? 25 : draft.type === 'long_text' ? 15000 : draft.type === 'email' ? 255 : 2048;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Add Field' : 'Edit Field'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Label"
              value={draft.label}
              onChange={(e) => handleLabelChange(e.target.value)}
              margin="dense"
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="API Name"
              value={draft.name}
              onChange={(e) => handleApiNameChange(e.target.value)}
              margin="dense"
              helperText="Auto-fills from label; edit to customize"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth label="Type" value={draft.type} onChange={(e) => update({ type: e.target.value })} margin="dense">
              {FIELD_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={!!draft.required} onChange={(e) => update({ required: e.target.checked })} />}
              label="Required"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Placeholder"
              value={draft.placeholder || ''}
              onChange={(e) => update({ placeholder: e.target.value })}
              margin="dense"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Helper text"
              value={draft.helpText || ''}
              onChange={(e) => update({ helpText: e.target.value })}
              margin="dense"
            />
          </Grid>
          {TYPES_WITH_MAX_LENGTH.includes(draft.type) && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max length"
                value={draft.maxLength ?? ''}
                onChange={(e) => update({ maxLength: e.target.value === '' ? undefined : Number(e.target.value) })}
                margin="dense"
                helperText={`Optional. Default limit for ${draft.type}: ${defaultMaxHint}`}
                inputProps={{ min: 1, max: defaultMaxHint }}
              />
            </Grid>
          )}
          {['dropdown', 'select_list'].includes(draft.type) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Add option"
                value={optionText}
                onChange={(e) => setOptionText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && optionText.trim()) {
                    e.preventDefault();
                    update({ options: [...(draft.options || []), optionText.trim()] });
                    setOptionText('');
                  }
                }}
                margin="dense"
                helperText="Press Enter to add an option"
              />
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                {(draft.options || []).map((opt, idx) => (
                  <Chip
                    key={`${opt}-${idx}`}
                    label={opt}
                    onDelete={() => update({ options: draft.options.filter((_, i) => i !== idx) })}
                  />
                ))}
              </Box>
            </Grid>
          )}
          {['relation', 'multirelation'].includes(draft.type) && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Custom Entity"
                value={draft.referencedObject || 'lead'}
                onChange={(e) => update({ referencedObject: e.target.value })}
                margin="dense"
                helperText="Choose which object this field links to, including your own Custom Objects"
              >
                <ListSubheader>Standard Objects</ListSubheader>
                {relationTargets.filter((t) => t.group === 'Standard Objects').map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
                <ListSubheader>Custom Entities</ListSubheader>
                {relationTargets.filter((t) => t.group === 'Custom Entities').length ? (
                  relationTargets
                    .filter((t) => t.group === 'Custom Entities')
                    .map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))
                ) : (
                  <MenuItem disabled value="">
                    No custom objects yet
                  </MenuItem>
                )}
              </TextField>
            </Grid>
          )}
          {draft.type === 'currency' && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Default Currency"
                value={draft.currencyCode || 'USD'}
                onChange={(e) => update({ currencyCode: e.target.value })}
                margin="dense"
              >
                {ISO_CURRENCIES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{formatCurrencyOption(c)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {['boolean', 'checkbox'].includes(draft.type) && (
            <>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Label preset"
                  value={`${draft.trueLabel || 'Yes'}|${draft.falseLabel || 'No'}`}
                  onChange={(e) => {
                    const [trueLabel, falseLabel] = e.target.value.split('|');
                    update({ trueLabel, falseLabel });
                  }}
                  margin="dense"
                  helperText="Choose a common Yes/No style pair, or edit labels below"
                >
                  {BOOLEAN_LABEL_PRESETS.map((p) => (
                    <MenuItem key={`${p.trueLabel}|${p.falseLabel}`} value={`${p.trueLabel}|${p.falseLabel}`}>
                      {p.trueLabel} / {p.falseLabel}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="True / Yes label"
                  value={draft.trueLabel || 'Yes'}
                  onChange={(e) => update({ trueLabel: e.target.value })}
                  margin="dense"
                  placeholder="e.g. Yes, True, Active"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="False / No label"
                  value={draft.falseLabel || 'No'}
                  onChange={(e) => update({ falseLabel: e.target.value })}
                  margin="dense"
                  placeholder="e.g. No, False, Inactive"
                />
              </Grid>
              {draft.type === 'boolean' && (
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Display as"
                    value={draft.booleanDisplay || 'switch'}
                    onChange={(e) => update({ booleanDisplay: e.target.value })}
                    margin="dense"
                  >
                    {BOOLEAN_DISPLAY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {draft.type === 'checkbox' && (
                <Grid item xs={12}>
                  <Alert severity="info">Checkbox fields always show as a checkbox on forms.</Alert>
                </Grid>
              )}
            </>
          )}
          {draft.type === 'list_of_texts' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Delimiter"
                value={draft.delimiter || ','}
                onChange={(e) => update({ delimiter: e.target.value })}
                margin="dense"
              />
            </Grid>
          )}
          {draft.type === 'long_text' && (
            <Grid item xs={12}>
              <Alert severity="info">Rich text editing is enabled on record forms.</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton color="info" onClick={handleSave}>Save Field</MDButton>
      </DialogActions>
    </Dialog>
  );
}
